import { ArrowLeftIcon, PhotoIcon, UserCircleIcon, ArrowUpTrayIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { t, use } from 'i18next'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { useLazyQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { GET_COUNTRY_LIST, GET_LOCATION_LIST } from 'graphql/common/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'

const customIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // URL to a marker icon (example: a pin icon)
    iconSize: [35, 35], // Size of the icon
    iconAnchor: [17, 34], // Position to anchor the icon
    popupAnchor: [0, -34], // Position for popup to open
})

export default function LocationDetails() {
    const { id } = useParams()
    console.log('Location ID: ', id)
    const bahrainCoordinates = [26.0667, 50.5577]
    const { t } = useTranslation()
    const [locationInfo, setLocationInfo] = useState(null)
    const [countries, setCountries] = useState([])

    const [getLocationList, { loading: locationListLoading, error: locationListError }] = useLazyQuery(
        GET_LOCATION_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchLocation.success && data.searchLocation.code === '200') {
                    setLocationInfo(data.searchLocation.locations[0])
                }
            },
        }
    )

    useEffect(() => {
        getLocationList({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                searchString: null,
                searchLocationId: id,
            },
        })
    }, [id])

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.country) {
                setCountries(data.country)
            }
        },
    })

    useEffect(() => {
        getCountryList({
            variables: { code: null },
        })
    }, [])

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <div className="flex">
                    <button
                        onClick={() => {
                            window.history.back()
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-semibold leading-7 text-primary ps-2 pb-1"> {t('Location details')}</h3>
                </div>
                <a
                    href={`/location/location-form/${locationInfo?.id}`}
                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    {t('Edit Location')}
                </a>
            </div>
            {locationListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {locationListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch locations!')}</div>
                </div>
            )}

            {!locationListLoading && !locationListError && locationInfo && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="mt-5">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary"> {t('Location Name')}</h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {' '}
                                            {t('English name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo.name.en}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Arabic Name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo.name.ar}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Address Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Area Name')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.address?.area}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Road Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.address?.road_no}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {' '}
                                            {t('Unit Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.address?.unit_no}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {' '}
                                            {t('Block Number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.address?.block_no}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {' '}
                                            {t('Landmark')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.address?.landmark}
                                        </dd>
                                    </div>

                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Country')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.country_details?.country_code && (
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={`https://flagcdn.com/w20/${locationInfo?.country_details?.country_code.toLowerCase()}.png`}
                                                        alt={locationInfo?.country_details?.country_code}
                                                        className="w-5 h-3"
                                                        onError={e => {
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />

                                                    {countries
                                                        .filter(
                                                            country =>
                                                                country.alpha_iso2 ===
                                                                locationInfo?.country_details?.country_code
                                                        )
                                                        .map(country => (
                                                            <span key={country.alpha_iso2}>
                                                                {country.country} ({country.country_ar})
                                                            </span>
                                                        ))}
                                                </div>
                                            )}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Contact Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Email ')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.contact?.email}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Phone number')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.contact?.phone_number}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">{t('Coordinates')}</h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Latitude ')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.geo?.lat}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Longitude ')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {locationInfo?.geo?.lng}
                                        </dd>
                                    </div>
                                    {/* <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {' '}
                                            {t('Map Location')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <div className="w-full max-w-4xl h-96 rounded-lg shadow-lg overflow-hidden">
                                                <MapContainer
                                                    center={bahrainCoordinates}
                                                    zoom={10}
                                                    className="w-full h-full"
                                                >
                                                    <TileLayer
                                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                    />
                                                    <Marker position={bahrainCoordinates} icon={customIcon}>
                                                        <Popup>
                                                            <strong>Bahrain</strong>
                                                            <br />
                                                            Coordinates: 26.0667, 50.5577
                                                        </Popup>
                                                    </Marker>
                                                </MapContainer>
                                            </div>
                                        </dd>
                                    </div> */}
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
